/**
 * @module ThemeContext
 * @description Contexto global para gestión del tema visual (claro/oscuro) de la aplicación.
 * Persiste la preferencia del usuario en localStorage y respeta la preferencia del sistema
 * operativo (prefers-color-scheme) como valor inicial cuando no hay preferencia guardada.
 *
 * La clase CSS "dark" se agrega/elimina en el elemento <html> (documentElement),
 * lo que permite que Tailwind CSS aplique las variantes `dark:` en toda la aplicación.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Contrato del contexto de tema.
 * @property isDark - `true` si el tema oscuro está activo actualmente.
 * @property toggleTheme - Alterna entre tema claro y oscuro.
 */
interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

/** Contexto interno — no exportado directamente; se accede a través de `useTheme()`. */
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Proveedor de tema. Debe envolver la raíz de la aplicación (o al menos todos los
 * componentes que necesiten conocer o modificar el tema).
 *
 * Lógica de inicialización:
 * 1. Si existe `theme` en localStorage → usa ese valor.
 * 2. Si no existe, consulta `window.matchMedia('prefers-color-scheme: dark')`.
 * 3. En SSR/entorno sin `window`, cae a tema claro.
 *
 * @param children - Árbol de componentes React a envolver.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDark, setIsDark] = useState(() => {
    // La función de inicialización lazy evita leer localStorage en cada render.
    // Se ejecuta solo una vez al montar el componente.
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' ||
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  /**
   * Efecto sincronizador: mantiene la clase CSS "dark" y localStorage
   * en sincronía con el estado `isDark` cada vez que cambia.
   */
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  /** Alterna el estado del tema entre claro y oscuro. */
  const toggleTheme = () => {
    setIsDark(!isDark);
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook para consumir el contexto de tema desde cualquier componente hijo de `ThemeProvider`.
 *
 * @throws Error si se usa fuera de `ThemeProvider`.
 * @returns `{ isDark, toggleTheme }` — estado actual del tema y función de alternancia.
 *
 * @example
 * const { isDark, toggleTheme } = useTheme();
 * return <button onClick={toggleTheme}>{isDark ? 'Claro' : 'Oscuro'}</button>;
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
